import { SIDEBAR_NAV } from "@/lib/constants/routes";

import { resolveNavLabel, type TranslateFn } from "./resolve-nav-label";

export interface LocalizableNavigationItem {
  id: string;
  label: string;
  labelKey?: string;
  icon: string;
  path: string;
  section?: string;
  parentPath?: string;
  visibility?: string;
  requiredPermissions?: string[];
  hrmSubgroup?: string;
  navModule?: string;
  children?: LocalizableNavigationItem[];
}

const LABEL_KEY_BY_PATH = new Map(
  SIDEBAR_NAV.map((item) => [item.href.split("?")[0], item.labelKey]),
);

function navigationPathKey(path: string): string {
  return path.split("?")[0];
}

const CHROME_TITLE_PATH_ALIASES: Readonly<Record<string, string>> = {
  "/llm-privacy": "/rules/llm-privacy",
  "/llm-runtime": "/ai/runtime",
  "/admin/llm-runtime": "/ai/runtime",
};

const CHROME_TITLE_LABEL_KEYS: Readonly<Record<string, string>> = {
  "/rules": "nav.policyRules",
  "/admin": "route.governance.title",
  "/performance": "analytics.tabPerformance",
  "/analytics/dashboard": "nav.platformAnalytics",
  "/database": "platformOps.database.title",
  "/database/management": "platformOps.database.title",
  "/services": "platformOps.services.title",
  "/deployment": "platformOps.deployment.title",
  "/ai/conversation": "ai.conversation.chromeTitle",
  "/ai/code-generator": "ai.code.chromeTitle",
};

export function resolvePathChromeTitle(path: string, t: TranslateFn, fallbackLabel: string): string {
  const pathKey = navigationPathKey(path);
  if (pathKey.startsWith("/iam/tenants/") && pathKey !== "/iam/tenants") {
    return resolveNavLabel(t, { labelKey: "iam.tenantDetailTitle", fallbackLabel });
  }
  const aliasedPath = CHROME_TITLE_PATH_ALIASES[pathKey] ?? pathKey;
  const pathLabelKey =
    CHROME_TITLE_LABEL_KEYS[aliasedPath] ??
    CHROME_TITLE_LABEL_KEYS[pathKey] ??
    LABEL_KEY_BY_PATH.get(aliasedPath) ??
    LABEL_KEY_BY_PATH.get(pathKey);
  return resolveNavLabel(t, { labelKey: pathLabelKey, fallbackLabel });
}

export function resolveNavigationItemLabel(item: LocalizableNavigationItem, t: TranslateFn): string {
  if (item.labelKey) {
    return resolveNavLabel(t, { labelKey: item.labelKey, fallbackLabel: item.label });
  }
  const pathKey = navigationPathKey(item.path);
  if (pathKey === "/" || item.id === "dashboard") {
    return resolveNavLabel(t, { labelKey: "nav.dashboard", fallbackLabel: item.label });
  }
  // Parent modules often share a landing path with their first child; path-based palette keys are leaf-only.
  if (item.children?.length) {
    return item.label;
  }
  const pathLabelKey = LABEL_KEY_BY_PATH.get(pathKey);
  return resolveNavLabel(t, { labelKey: pathLabelKey, fallbackLabel: item.label });
}

export function localizeNavigationItems(items: LocalizableNavigationItem[], t: TranslateFn): LocalizableNavigationItem[] {
  return items.map((item) => ({
    ...item,
    label: resolveNavigationItemLabel(item, t),
    children: item.children ? localizeNavigationItems(item.children, t) : undefined,
  }));
}
